import { UserAuthState } from '@app-state';
import {
    FeatureFlag,
    FolderType,
    IndividuallyLicensedFeature,
} from '@models';

import * as hash from 'object-hash';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';

export interface IPageMetadata {
    title: string;
    translationKey: string;
    routerLink: string;
    category: PageCategory;
    scope: PageScope;
    priority?: number;
    folderTypeDependency?: FolderType;
    folderTypeDependencyMessage?: string;
    permission?: string;
    featureFlag?: FeatureFlag;
    licenseFlag?: IndividuallyLicensedFeature;
    type?: 'Sidebar' | 'Admin' | 'SubMenu' | 'Breadcrumb' | 'None';
    sidebarSection?: SidebarSectionType;
    icon?: string;
    svgIcon?: string;
    subtitle?: string;
    parentRoute?: string;
    isWhitelisted?: boolean;
}

export type SidebarSectionType = 'Monitoring' | 'Automations' | 'Management';
export const SIDEBAR_SECTION_TYPES: ReadonlyArray<SidebarSectionType> = ['Monitoring', 'Automations', 'Management'];

export interface ISidebarSectionMetadata {
    fontIcon?: string;
    svgIcon?: string;
    translateKey: string;
}

export interface ISidebarSection {
    metadata?: ISidebarSectionMetadata;
    pages: PageMetadata[];
}

export enum PageCategory {
    Host,
    Tenant,
    Unauthorized,
    All,
}

export enum PageScope {
    Global,
    Folder,
}

export const SIDEBAR_SECTION_METADATA: Readonly<Record<SidebarSectionType, ISidebarSectionMetadata>> = {
    Management: {
        translateKey: 'sidebar_section_management',
        svgIcon: 'uipath:global',
    },
    Automations: {
        translateKey: 'sidebar_section_automations',
        fontIcon: 'folder',
    },
    Monitoring: {
        translateKey: 'sidebar_section_monitoring',
        fontIcon: 'folder',
    },
};

export const convertToCategory = (state: UserAuthState) => {
    switch (state) {
        case UserAuthState.NotLoggedIn:
            return PageCategory.Unauthorized;

        case UserAuthState.LoggedInAsTenant:
            return PageCategory.Tenant;

        case UserAuthState.LoggedInAsHost:
            return PageCategory.Host;

        default:
            return PageCategory.All;
    }
};

export class PageMetadata implements IPageMetadata {
    public identifier: string;
    public title: string;
    public translationKey: string;
    public routerLink: string;
    public category: PageCategory;
    public type: IPageMetadata['type'];
    public isWhitelisted: boolean;
    public folderFallbackUrl?: string;
    public folderTypeDependency?: FolderType;
    public sidebarSection?: SidebarSectionType;
    public permission?: string;
    public featureFlag?: FeatureFlag;
    public licenseFlag?: IndividuallyLicensedFeature;
    public icon?: string;
    public svgIcon?: string;
    public subtitle?: string;
    public parent?: PageMetadata;
    public scope: PageScope;
    public priority: number;
    public folderTypeDependencyMessage?: string;

    public get hasParent(): boolean {
        return !!this.parent;
    }

    public set customTitle(value: string) {
        (this.customTitle$ as BehaviorSubject<string>).next(value);
    }

    public readonly cleanRouterLink: string;

    public customTitle$: Observable<string> = new BehaviorSubject('');

    constructor(
        {
            title,
            translationKey,
            routerLink,
            category,
            permission,
            featureFlag,
            licenseFlag,
            folderTypeDependency,
            type,
            icon,
            svgIcon,
            subtitle,
            isWhitelisted,
            scope,
            priority,
            sidebarSection,
            folderTypeDependencyMessage,
        }: IPageMetadata,
        parent?: IPageMetadata,
    ) {
        this.title = title;
        this.translationKey = translationKey;
        this.routerLink = routerLink;
        this.category = category;
        this.folderTypeDependency = folderTypeDependency;
        this.sidebarSection = sidebarSection;
        this.type = type || 'None';
        this.isWhitelisted = !!isWhitelisted;
        this.featureFlag = featureFlag;
        this.licenseFlag = licenseFlag;
        this.icon = icon;
        this.svgIcon = svgIcon;
        this.subtitle = subtitle;
        this.permission = permission;
        this.scope = scope;
        this.priority = priority != null ? priority : Number.MAX_SAFE_INTEGER;
        this.folderTypeDependencyMessage = folderTypeDependencyMessage;

        this.cleanRouterLink = this._removeRouteParam(this.routerLink);

        this.parent = !!parent ? new PageMetadata(parent) : undefined;

        this.identifier = hash.sha1({
            title,
            translationKey,
            routerLink,
        });
    }

    public isMatch(url: string) {
        const currentParts = this.routerLink.split('/');
        const urlParts = url.split('/');

        if (currentParts.length < urlParts.length) { return false; }

        return currentParts.every((part, idx) => (part.startsWith(':') || part === urlParts[idx]));
    }

    public equals(page: PageMetadata) {
        return this === page ||
            this.routerLink === page.routerLink;
    }

    public isChildOf(page: PageMetadata) {
        return this.parent &&
            this.parent.routerLink === page.routerLink;
    }

    public isAccessibleByFolder(type: FolderType | null) {
        if (!this.folderTypeDependency) { return true; }

        return this.folderTypeDependency === type;
    }

    private _removeRouteParam = (link: string) => {
        const lastPartIdx = link.lastIndexOf('/');
        if (
            lastPartIdx + 1 === link.indexOf(':')
        ) {
            link = link.substring(0, lastPartIdx);
        }
        return link;
    }
}
